import type React from "react";
import { useId } from "react";
import { useState } from "react";
import { Tooltip as ReactTooltip } from "react-tooltip";
import { EyeClosedIcon } from "~/components/icons/EyeClosedIcon";
import { EyeIcon } from "~/components/icons/EyeIcon";

interface TextFieldProps extends React.InputHTMLAttributes<HTMLInputElement> {
	name: string;
	label?: string;
	placeholder?: string;
	tooltip?: React.ReactNode;
	inputType?: "text" | "email" | "password" | "date" | "number" | "tel";
	display?: "stacked" | "inline";
	bgStyle?: "transparent" | "filled";
	required?: boolean;
	invalid?: boolean;
	valid?: boolean;
	theme?: Theme;
}

export const TextField = ({
	name,
	label,
	placeholder,
	tooltip,
	inputType = "text",
	display = "stacked",
	bgStyle = "transparent",
	required = false,
	invalid = false,
	valid = false,
	theme = "light",
	...extraInputProps
}: TextFieldProps) => {
	const id = useId();
	const [showPassword, setShowPassword] = useState(false);
	const [currentInputType, setCurrentInputType] = useState(inputType);

	const toggleShowPassword = () => {
		setShowPassword(!showPassword);
		setCurrentInputType(currentInputType === "password" ? "text" : "password");
	};

	return (
		<div
			className={`flex gap-2 flex-col ${
				display === "inline" ? "sm:flex-row sm:items-center" : ""
			}`}
		>
			{label && (
				<label
					htmlFor={name}
					className={`text-secondary-900 ${display === "inline" ? "sm:w-1/3" : ""}`}
				>
					{label}
					{required ? (
						""
					) : (
						<span className="ml-1 text-secondary-500">(optional)</span>
					)}
				</label>
			)}
			<div className={`relative ${display === "inline" ? "sm:w-2/3" : ""}`}>
				<input
					className={`border p-2 w-full ${theme === "light" ? `text-gray-900 ${bgStyle === "transparent" ? "bg-transparent" : "bg-white"}` : "text-white bg-secondary-dark-input-bg"} rounded ${
						invalid
							? "border-primary ring-primary-200 ring-4"
							: valid
								? "border-positive-500 ring-positive-100 ring-4"
								: theme === "light"
									? "border-secondary-200"
									: "border-secondary-700 placeholder:text-secondary-400"
					}`}
					name={name}
					placeholder={placeholder}
					aria-label={name}
					type={currentInputType}
					{...extraInputProps}
					data-tooltip-id={`tooltip-${id}`}
				/>
				{inputType === "password" ? (
					// biome-ignore lint/a11y/useKeyWithClickEvents: <explanation>
					<span
						className="absolute right-4 top-2 text-gray-400 cursor-pointer"
						onClick={toggleShowPassword}
					>
						{showPassword ? <EyeIcon /> : <EyeClosedIcon />}
					</span>
				) : (
					""
				)}
				{tooltip && (
					<ReactTooltip
						id={`tooltip-${id}`}
						className="text-left !bg-secondary !rounded-lg !text-white"
						place={"top"}
						openEvents={{ focus: true }}
						closeEvents={{ blur: true }}
					>
						{tooltip}
					</ReactTooltip>
				)}
			</div>
		</div>
	);
};
